import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";

import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";

import {
  BORDER_COLOR,
  TEXT_MUTED,
  StatusChip,
  Dot,
  SummaryCard,
} from "./Shared.component";

import Theme from "../utils/Theme";

import { INSURANCE_REPORT } from "../graphql/query/query";

// Header background (same as Manage User table header)
const HEADER_BG = "#F4F6F8";

// Card shadow used by the Manage User table
const PAPER_SHADOW =
  "0px 0px 2px 0px rgba(0, 171, 85, 0.2),0px 12px 24px -4px rgba(0, 171, 85, 0.12)";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const truncate = (value, maxLength = 14) => {
  if (!value) {
    return "—";
  }

  return value.length > maxLength
    ? `${value.slice(0, maxLength)}…`
    : value;
};

// Characters kept for the short, variable-length fields. Vehicle YMM and
// Follow Up Vehicle are intentionally NOT in this list: they show in full
// (their columns are sized to fit), with a CSS ellipsis + hover tooltip only
// as a safety net for unusually long values.
const CELL_MAX_CHARS = {
  customer: 14,
  cell: 16,
  email: 24,
  sales: 14,
};

// Shared sx for any cell that must never grow past its column width.
const clipSx = {
  fontSize: "13px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// -----------------------------------------------------------------------------
// Summary cards
// -----------------------------------------------------------------------------

const SUMMARY_KEYS = [
  { key: "deliveries", label: "Deliveries" },
  { key: "verifications", label: "Verifications" },
  { key: "initialAlerts", label: "Initial Alerts" },
  { key: "followUpVerifications", label: "Follow Up Verifications" },
  { key: "followUpAlerts", label: "Follow Up Alerts" },
  { key: "totalVerifications", label: "Total Verifications" },
];

// -----------------------------------------------------------------------------
// Columns
//
// The table uses tableLayout: "fixed", and in a fixed layout the widths are
// read from the HEADER row. So every column's width is set here and applied
// to the header cell (widths set only on body cells are ignored).
//
// compact: true = narrow column with tighter side padding (6px instead of
// 12px) and a header that is allowed to wrap onto two lines.
// -----------------------------------------------------------------------------

const COLUMNS = [
  { id: "purchaseDate", label: "Purchase Date", width: 100 },
  { id: "customer", label: "Customer", width: 120 },
  { id: "cell", label: "Cell", width: 115 },
  { id: "email", label: "Email", width: 165 },
  { id: "sales", label: "Sales", width: 100 },
  { id: "insAlert", label: "Ins Alert", width: 56, compact: true },
  { id: "verified", label: "Verified", width: 80 },
  { id: "vehicle", label: "Vehicle YMM", width: 190 },
  { id: "followUpVehicle", label: "Follow Up Vehicle", width: 290 },
  { id: "followUpDate", label: "Follow Up Date", width: 92, compact: true },
  { id: "followUpInsAlert", label: "Ins Alert", width: 56, compact: true },
];

const compactProps = (id) =>
  COLUMNS.find((c) => c.id === id)?.compact
    ? { className: "compact-cell" }
    : {};

const AlertDot = ({ color }) =>
  color ? (
    <Dot color={color} />
  ) : (
    <Typography
      component="span"
      sx={{ color: TEXT_MUTED, fontSize: "12px" }}
    >
      —
    </Typography>
  );

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const InsuranceTable = ({
  dealershipId,
  employeeUserId,
  dateRange,
  isEmployeeOnly = false,
  timezone = 0,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
  }, [
    dealershipId,
    employeeUserId,
    dateRange?.startDate,
    dateRange?.endDate,
    isEmployeeOnly,
  ]);

  const { data, loading, error } = useQuery(INSURANCE_REPORT, {
    variables: {
      reportInput: {
        dealership_id: dealershipId,
        employee_user_id: employeeUserId ?? null,
        f_startDate: dateRange?.startDate ?? null,
        f_endDate: dateRange?.endDate ?? null,
        pageCount: page + 1,
        rowsPerPage: rowsPerPage,
        isEmployeeOnly: isEmployeeOnly,
        timezone: timezone,
      },
    },
    skip:
      !dealershipId || !dateRange?.startDate || !dateRange?.endDate,
    fetchPolicy: "network-only",
  });

  const report = data?.insuranceReport;
  const summary = report?.summary || null;

  // Defensive client-side sort, newest purchaseDate first. Copy the array
  // first since report.rows may come from Apollo's cache and .sort() mutates.
  const rows = [...(report?.rows || [])].sort(
    (a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate)
  );

  const total = report?.total || 0;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error || report?.errors?.length) {
    const message =
      error?.message || report?.errors?.[0]?.message || "Unknown error";

    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography sx={{ color: "error.main", fontSize: "13px" }}>
          Failed to load insurance report: {message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Summary */}
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mb: 2 }}>
        {SUMMARY_KEYS.map(({ key, label }) => (
          <SummaryCard
            key={key}
            label={label}
            value={summary ? String(summary[key] ?? 0) : "0"}
          />
        ))}
      </Box>

      {/* Table — same card look as the Manage User table */}
      <Paper
        elevation={0}
        sx={{
          boxShadow: PAPER_SHADOW,
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          sx={{
            // Fixed height: pagination stays in the same place whether there
            // are 1 or 100 rows. Rows scroll inside when they don't fit.
            height: "calc(100vh - 430px)",
            minHeight: "250px",
            overflow: "auto",

            "& .MuiTableCell-root": {
              padding: "8px 12px",
              textAlign: "center",
            },
            // Tighter side padding for the narrow columns. Must be written
            // here (not in the cell's own sx) so it out-ranks the rule above.
            "& .MuiTableCell-root.compact-cell": {
              padding: "8px 6px",
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            {/* Header — widths live here (fixed table layout) */}
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell
                    key={column.id}
                    className={column.compact ? "compact-cell" : undefined}
                    sx={{
                      width: column.width,
                      fontWeight: 700,
                      fontSize: "12px",
                      color: "text.primary",
                      backgroundColor: HEADER_BG,
                      whiteSpace: column.compact ? "normal" : "nowrap",
                      lineHeight: column.compact ? 1.2 : undefined,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* Body */}
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    <Typography sx={{ fontSize: "13px", color: TEXT_MUTED }}>
                      No records found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  // Full text kept for the hover tooltip; nothing is lost.
                  const followUpVehicleText = (row.otherVehicles || []).map(
                    (vehicle) =>
                      `${vehicle.ymm || "—"}${
                        vehicle.vin ? ` · VIN ${vehicle.vin}` : ""
                      }`
                  );

                  return (
                    <TableRow hover key={`${row.customer}-${index}`}>
                      {/* Purchase Date */}
                      <TableCell sx={clipSx}>
                        {row.purchaseDate || "—"}
                      </TableCell>

                      {/* Customer */}
                      <TableCell
                        title={row.customer || ""}
                        sx={{
                          ...clipSx,
                          color: Theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        {truncate(row.customer, CELL_MAX_CHARS.customer)}
                      </TableCell>

                      {/* Cell */}
                      <TableCell title={row.cell || ""} sx={clipSx}>
                        {truncate(row.cell, CELL_MAX_CHARS.cell)}
                      </TableCell>

                      {/* Email */}
                      <TableCell title={row.email || ""} sx={clipSx}>
                        {truncate(row.email, CELL_MAX_CHARS.email)}
                      </TableCell>

                      {/* Sales */}
                      <TableCell title={row.sales || ""} sx={clipSx}>
                        {truncate(row.sales, CELL_MAX_CHARS.sales)}
                      </TableCell>

                      {/* Initial Ins Alert (now left of Verified) */}
                      <TableCell {...compactProps("insAlert")}>
                        <AlertDot color={row.insAlert} />
                      </TableCell>

                      {/* Verified */}
                      <TableCell>
                        <StatusChip value={row.verified || "No"} />
                      </TableCell>

                      {/* Vehicle YMM — purchased vehicle, shown in full */}
                      <TableCell
                        title={row.purchasedVehicleYMM || ""}
                        sx={clipSx}
                      >
                        {row.purchasedVehicleYMM || "—"}
                      </TableCell>

                      {/* Follow Up Vehicle — YMM + full VIN, one line each */}
                      <TableCell>
                        {followUpVehicleText.length > 0 ? (
                          followUpVehicleText.map((text, vIndex) => (
                            <Typography
                              key={`${row.customer}-followup-vehicle-${vIndex}`}
                              component="div"
                              title={text}
                              sx={clipSx}
                            >
                              {text}
                            </Typography>
                          ))
                        ) : (
                          <Typography
                            component="span"
                            sx={{ fontSize: "13px", color: TEXT_MUTED }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Follow Up Date */}
                      <TableCell
                        {...compactProps("followUpDate")}
                        sx={clipSx}
                      >
                        {row.followUpDate || "—"}
                      </TableCell>

                      {/* Follow-up Ins Alert */}
                      <TableCell {...compactProps("followUpInsAlert")}>
                        <AlertDot color={row.followUpInsAlert} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 8px",
            borderTop: `1px solid ${BORDER_COLOR}`,
            backgroundColor: "#fff",

            "& .MuiTablePagination-toolbar": {
              minHeight: "40px",
              padding: 0,
            },
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default InsuranceTable;